/**
 * Document-related API calls.
 *
 * Per project policy, network calls live in `src/services/` and use the shared
 * `apiClient` (which carries the base URL and the 402 → checkout interceptor).
 * Auth headers are resolved by the caller (the thunk) — mirroring the
 * owner-vs-shared / Bearer-vs-anonymous branching used by the document
 * download/get-text thunks — and passed in here.
 */
import apiClient from "src/services/apiClient";

export interface ConvertDocumentToGoogleDocArgs {
  threadId: string;
  docId: string;
  /** Document version to convert. `0` (original upload) is valid and must be sent. */
  version: number;
  /** True on a public/shared thread view — selects the `shared-` endpoint variant. */
  isShared: boolean;
  /** Pre-resolved auth headers (Bearer token or anonymous-user-id). */
  headers: Record<string, string>;
  /** Client timeout in ms. The call renders via pandoc + Drive round-trips (~1–3s). */
  timeout?: number;
}

export interface ConvertDocumentToGoogleDocResponse {
  google_doc_id: string;
}

/**
 * Convert a document version into an editable Google Doc and return its id.
 * Not idempotent — every call mints a new Google Doc.
 */
export const convertDocumentToGoogleDoc = async ({
  threadId,
  docId,
  version,
  isShared,
  headers,
  timeout = 30000,
}: ConvertDocumentToGoogleDocArgs): Promise<ConvertDocumentToGoogleDocResponse> => {
  // `version` may be 0 — build the query explicitly so the falsy value is kept.
  let url;
  if (isShared) {
    const pathParts = window.location.pathname.split("/");
    const sharedIndex = pathParts.indexOf("shared");
    const shareIdentifier = pathParts[sharedIndex + 1];
    url = `/api/thread/${shareIdentifier}/document/shared-convert-to-gdoc/${docId}?version=${version}`;
  } else {
    url = `/api/thread/${threadId}/document/convert-to-gdoc/${docId}?version=${version}`;
  }
  const { data } = await apiClient.post<ConvertDocumentToGoogleDocResponse>(url, null, {
    headers,
    timeout,
  });
  return { google_doc_id: data.google_doc_id };
};
